import React, { useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { Layout, Menu, Switch } from "antd";
import {
	BulbOutlined,
	MenuUnfoldOutlined,
	MenuFoldOutlined,
} from "@ant-design/icons";
import logoDarkMode from "../assets/images/logo-darkmode.png";
import logoLightMode from "../assets/images/logo-lightmode.png";
import logoMobDarkMode from "../assets/images/logo-mob-darkmode.png";
import logoMobLightMode from "../assets/images/logo-mob-lightmode.png";
import { useSelector } from "react-redux";
import routes from "../routes/routes";
import { translate } from "utils";
import { t } from "@lingui/macro";
import { selectMyProfile } from "store/auth/authSlice";
import { RouteEl } from "types";
import { APP_PATH } from "constants/path";

const { Sider } = Layout;

interface MenuItem extends Omit<RouteEl, "path" | "label"> {
	path?: string;
	label: React.ReactNode;
}

const keyWhiteList = [
	"config",
	"crm",
	"services",
	"notifications",
	"affiliate",
	"community",
	"loyalty",
	"ratings",
	"master-data",
	"webapp",
	"partner",
	'request-services',
];

const NavBar = (props: {
	collapsed: boolean;
	isMobile: boolean;
	setCollapsed: Function;
}) => {
	const myProfile = useSelector(selectMyProfile);
	const { permissions = [] } = myProfile;
	const params = useParams();
	const { pathname } = useLocation();
	const [key, setKey] = useState<string[]>([]);
	const [openKey, setOpenKey] = useState<string[]>([]);

	useEffect(() => {
		const pathParts = pathname.split("/");
		if (keyWhiteList.includes(pathParts[1])) {
			const openKeys = pathParts.slice(1);
			if (pathname.includes(APP_PATH.bannerConfig)) {
				openKeys.push("");
			}
			setOpenKey(openKeys);
			if (
				params.id ||
				pathParts[pathParts.length - 1].toLowerCase() === "create"
			)
				setKey([pathParts[pathParts.length - 2]]);
			else setKey([pathParts[pathParts.length - 1]]);
			return;
		}
		setKey([pathname.split("/")[1]]);
		setOpenKey(
			pathname.split("/")[1].split("-").length
				? [pathname.split("/")[1].split("-")[0]]
				: [pathname.split("/")[1]],
		);
	}, [pathname]);

	const setMenuItem = (routes: RouteEl[]): MenuItem[] => {
		const items: MenuItem[] = [];
		routes.forEach(route => {
			const {
				label,
				path,
				key,
				icon,
				children = [],
				hidden,
				permissions: routePerms = [],
			} = route;
			if (
				(!hidden &&
					permissions.find((el: string) =>
						routePerms.find((item: string) => el === item),
					)) ||
				(!hidden && !routePerms.length)
			) {
				let _children: MenuItem[] = [];

				if (children.length) {
					_children = setMenuItem(children);
				}

				items.push({
					key,
					icon,
					label: path ? (
						<Link to={path}>{translate(label)}</Link>
					) : (
						translate(label)
					),
					...(_children.length && { children: _children }),
				} as MenuItem);
			}
		});
		return items;
	};

	const menuItems: MenuItem[] = setMenuItem(routes);

	const { collapsed, isMobile, setCollapsed } = props;
	const [darkMode, setDarkMode] = useState<boolean>(true);

	useEffect(() => {
		collapsed && setOpenKey([]);
	}, [pathname]);

	return (
		<Sider
			collapsible
			collapsed={collapsed}
			className="fixed top-0 left-0 z-40 h-screen overflow-y-scoll"
			trigger={null}
			theme={darkMode ? "dark" : "light"}
			width={280}
			style={{ left: isMobile && collapsed ? "-80px" : 0 }}
		>
			<span
				onClick={() => setCollapsed(!collapsed)}
				className={`absolute top-4 right-[-40px] w-50 ${
					!collapsed && isMobile ? "hidden" : ""
				}`}
			>
				{collapsed ? (
					<MenuUnfoldOutlined className="cursor-pointer text-xl opacity-60" />
				) : (
					<MenuFoldOutlined className="cursor-pointer text-xl opacity-60" />
				)}
			</span>
			<div className="overflow-y-auto h-[90%] menu-wrapper">
				<Link
					to="/dashboard"
					className="flex justify-center items-center h-16 shadow-[0_1px_9px_-3px_rgba(0,0,0,0.2)] mt-4"
				>
					<img
						src={
							collapsed
								? darkMode
									? logoMobLightMode
									: logoMobDarkMode
								: darkMode
								? logoLightMode
								: logoDarkMode
						}
						className={`${
							collapsed ? "h-1/2" : "h-3/4 p-2"
						} max-w-[220px] box-border`}
						alt="logo"
					/>
				</Link>
				<Menu
					theme={darkMode ? "dark" : "light"}
					selectedKeys={key}
					openKeys={openKey}
					mode="inline"
					className="h-auto overflow-x-hiden mt-5 mb-10"
					items={menuItems}
					onOpenChange={openKeys => {
						setOpenKey(openKeys);
					}}
					onClick={({ item, key }) => {
						setKey([key]);
					}}
				/>
			</div>
			{!collapsed ? (
				<div
					className="px-4 w-full absolute flex items-center justify-between"
					style={{ bottom: "15px" }}
				>
					<div style={{ color: "#666666" }} className="flex items-center">
						<BulbOutlined style={{ fontSize: "16px" }} className="mr-1" />
						<span>{t`Switch theme`}</span>
					</div>
					<Switch
						checkedChildren={t`Dark`}
						unCheckedChildren={t`Light`}
						checked={darkMode}
						style={{
							backgroundColor: darkMode ? "#1890ff" : "#cccccc",
						}}
						onChange={check => setDarkMode(check)}
					/>
				</div>
			) : null}
		</Sider>
	);
};

export default NavBar;
